import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import { setApprovalAnnouncer } from '../services/approvalBridge';

const POLL_MS = 60 * 1000;
const TOAST_MS = 10 * 1000;

const ApprovalContext = createContext({ open: [], summary: { pending: 0, denied: 0 }, refresh: () => {}, waitingOn: () => [] });

// What is waiting for an admin's decision (`open`, for the "esperando aprobación" chips on the record lists), the number on the
// header bell (`summary`), and `refresh()` to re-read both after something changed.
export const useApprovals = () => useContext(ApprovalContext);

// Mounted once, inside the router: keeps the waiting-approvals data fresh (on every page change, when the window regains
// focus and every minute) and shows the toast that tells a sales user their request was passed to the admins.
const ApprovalProvider = ({ children }) => {
  const { isLoggedIn, mustChangePassword } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, denied: 0 });
  const [toasts, setToasts] = useState([]);
  const seq = useRef(0);

  const refresh = useCallback(async () => {
    if (!isLoggedIn || mustChangePassword || !localStorage.getItem('token')) { setOpen([]); setSummary({ pending: 0, denied: 0 }); return; }
    try {
      const [o, s] = await Promise.all([API.get('/approvals/open', { silent: true }), API.get('/approvals/summary', { silent: true })]);
      setOpen(o.data); setSummary(s.data);
    } catch (err) { /* keep what was there; the next tick tries again */ }
  }, [isLoggedIn, mustChangePassword]);

  useEffect(() => { refresh(); }, [refresh, location.pathname]);
  useEffect(() => {
    const timer = setInterval(refresh, POLL_MS);
    window.addEventListener('focus', refresh);
    return () => { clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [refresh]);

  const dismiss = useCallback((id) => setToasts(list => list.filter(t => t.id !== id)), []);

  useEffect(() => {
    setApprovalAnnouncer((data) => {
      refresh();
      if (data.action === 'invoices.send-ecf') return; // the invoice itself shows it (Facturas), with its own message
      const id = ++seq.current;
      setToasts(list => [...list, { id, text: data.toast || data.message, note: data.emailNote }]);
      setTimeout(() => dismiss(id), TOAST_MS);
    });
    return () => setApprovalAnnouncer(null);
  }, [refresh, dismiss]);

  const byEntity = useMemo(() => {
    const map = new Map();
    open.forEach(r => map.set(r.entityId, [...(map.get(r.entityId) || []), r]));
    return map;
  }, [open]);
  const waitingOn = useCallback((entityId) => byEntity.get(String(entityId)) || [], [byEntity]);

  const value = useMemo(() => ({ open, summary, refresh, waitingOn }), [open, summary, refresh, waitingOn]);

  return (
    <ApprovalContext.Provider value={value}>
      {children}
      <div style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 2000, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 420 }}>
        {toasts.map(t => (
          <div key={t.id} role="status" className="p-3 d-flex align-items-start gap-3"
            style={{ background: 'var(--mm-panel-2)', border: '1px solid var(--mm-cyan)', borderLeft: '4px solid var(--mm-cyan)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.45)' }}>
            <i className="bi bi-hourglass-split" style={{ color: 'var(--mm-cyan)', fontSize: '1.3rem' }} />
            <div style={{ minWidth: 0 }}>
              <div>{t.text}</div>
              {t.note && <div className="small text-muted mt-1">{t.note}</div>}
              <button type="button" className="btn btn-link p-0 small" style={{ color: 'var(--mm-cyan)' }}
                onClick={() => { dismiss(t.id); navigate('/aprobaciones'); }}>Ver mis solicitudes</button>
            </div>
            <button type="button" className="btn btn-link p-0 ms-auto" aria-label="Cerrar" style={{ color: 'var(--mm-muted)' }} onClick={() => dismiss(t.id)}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
        ))}
      </div>
    </ApprovalContext.Provider>
  );
};

export default ApprovalProvider;
